import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { FocusGoal } from '../../hooks/useGoals';
import { GoalRubricMapping } from './MappingModal';
import { motion } from 'framer-motion';
import { IoExpandOutline, IoContractOutline } from 'react-icons/io5';

interface MappingPreviewProps {
  goals: FocusGoal[];
  rubricCategories: any[]; // TODO: Add proper type
  mappings: GoalRubricMapping[];
  onBack: () => void;
  hideHeader?: boolean; // Optional prop to hide the header
}

interface Position {
  x: number;
  y: number;
}

interface ConnectionPoint {
  id: string;
  type: 'goal' | 'rubric';
  position: Position;
}

const MappingPreview: React.FC<MappingPreviewProps> = ({ 
  goals, 
  rubricCategories, 
  mappings, 
  onBack,
  hideHeader = false 
}) => {
  const { darkMode } = useDarkMode();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [connectionPoints, setConnectionPoints] = useState<Map<string, ConnectionPoint>>(new Map());
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [selectedRubricItem, setSelectedRubricItem] = useState<string | null>(null);
  const [isZoomedOut, setIsZoomedOut] = useState(false);

  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const selectedBg = darkMode ? 'bg-gray-700' : 'bg-blue-50';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-300';

  // Goal emoji icons
  const goalEmojis: Record<string, string> = {
    return_offer: '🎯',
    skill_learning: '📚',
    networking: '🤝',
    portfolio: '💼',
    custom: '🚀'
  };

  // Goal colors for connections
  const goalColors: Record<string, string> = {
    return_offer: '#3b82f6', // blue
    skill_learning: '#8b5cf6', // purple
    networking: '#10b981', // green
    portfolio: '#f59e0b', // amber
    custom: '#6b7280' // gray
  };

  // Calculate connection points when component mounts or updates
  const updateConnectionPoints = useCallback(() => {
    if (!canvasRef.current) return;

    const newPoints = new Map<string, ConnectionPoint>();
    const canvasRect = canvasRef.current.getBoundingClientRect();

    // Calculate goal connection points
    goals.forEach((goal) => {
      const goalElement = document.getElementById(`preview-goal-${goal.id}`);
      if (goalElement) {
        const rect = goalElement.getBoundingClientRect();
        // Find the connection dot within the goal element
        const dotElement = goalElement.querySelector('.connection-dot-goal');
        const dotOffset = dotElement ? dotElement.getBoundingClientRect() : rect;
        
        newPoints.set(`goal-${goal.id}`, {
          id: goal.id,
          type: 'goal',
          position: {
            x: dotOffset.left + dotOffset.width / 2 - canvasRect.left,
            y: dotOffset.top + dotOffset.height / 2 - canvasRect.top
          }
        });
      }
    });

    // Calculate rubric connection points
    rubricCategories.forEach((category) => {
      category.items.forEach((item: any) => {
        const itemElement = document.getElementById(`preview-rubric-${category.id}-${item.id}`);
        if (itemElement) {
          const rect = itemElement.getBoundingClientRect();
          // Find the connection dot within the rubric item
          const dotElement = itemElement.querySelector('.connection-dot-rubric');
          const dotOffset = dotElement ? dotElement.getBoundingClientRect() : rect;
          
          newPoints.set(`rubric-${category.id}-${item.id}`, {
            id: `${category.id}-${item.id}`,
            type: 'rubric',
            position: {
              x: dotOffset.left + dotOffset.width / 2 - canvasRect.left,
              y: dotOffset.top + dotOffset.height / 2 - canvasRect.top
            }
          });
        }
      });
    });

    setConnectionPoints(newPoints);
  }, [goals, rubricCategories]);

  useEffect(() => {
    updateConnectionPoints();
    // Update on resize
    window.addEventListener('resize', updateConnectionPoints);
    return () => window.removeEventListener('resize', updateConnectionPoints);
  }, [updateConnectionPoints]);

  // Update connection points when zoom state changes
  useEffect(() => {
    let animationId: number;
    let startTime: number;
    const duration = 300; // Match the CSS transition duration

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      updateConnectionPoints();

      if (elapsed < duration) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isZoomedOut, updateConnectionPoints]);

  const handleGoalClick = (goalId: string) => {
    setSelectedGoalId(selectedGoalId === goalId ? null : goalId);
    setSelectedRubricItem(null);
  };

  const handleRubricItemClick = (categoryId: string, itemId: string) => {
    const fullId = `${categoryId}-${itemId}`;
    setSelectedRubricItem(selectedRubricItem === fullId ? null : fullId);
    setSelectedGoalId(null);
  };

  const isConnectionActive = (goalId: string, categoryId: string, itemId: string) => {
    // If nothing is selected, all connections are "active" (visible)
    if (!selectedGoalId && !selectedRubricItem) return true;
    
    // If a goal is selected, show its connections
    if (selectedGoalId === goalId) return true;
    
    // If a rubric item is selected, show connections to it
    if (selectedRubricItem === `${categoryId}-${itemId}`) return true;
    
    return false;
  };

  const getConnectionsForGoal = (goalId: string) => {
    return mappings.filter(m => m.goal_id === goalId);
  };

  const getGoalIdForRubricItem = (categoryId: string, itemId: string) => {
    const mapping = mappings.find(m => 
      m.rubric_category === categoryId && m.rubric_items.includes(itemId)
    );
    return mapping?.goal_id;
  };

  return (
    <div>
      {/* Back Button - Only show if not hidden */}
      {!hideHeader && (
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={onBack}
            className={`text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
          >
            ← Back to Edit
          </button>
          <p className={`text-sm ${mutedTextColor}`}>
            Click on goals or criteria to highlight connections
          </p>
        </div>
      )}

      {/* Main Preview Container */}
      <div className="relative">
        {/* Zoom Toggle Button */}
        <button
          onClick={() => setIsZoomedOut(!isZoomedOut)}
          className={`absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-2 rounded-lg ${
            darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700'
          } shadow-lg transition-colors`}
          title={isZoomedOut ? 'Default view' : 'View all'}
        >
          {isZoomedOut ? <IoContractOutline className="w-5 h-5" /> : <IoExpandOutline className="w-5 h-5" />}
          <span className="text-sm font-medium">{isZoomedOut ? 'Default View' : 'View All'}</span>
        </button>

        <div ref={canvasRef} className={`relative ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'} rounded-xl ${isZoomedOut ? 'p-4' : 'p-8'} border ${borderColor} overflow-auto transition-all duration-300`}
          style={isZoomedOut ? { maxHeight: '600px' } : {}}
        >
        {/* SVG Connections Layer */}
        <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
          <defs>
            {/* Create gradient definitions for each goal color */}
            {Object.entries(goalColors).map(([type, color]) => (
              <linearGradient key={type} id={`gradient-${type}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={color} stopOpacity="0.6" />
                <stop offset="100%" stopColor={color} stopOpacity="0.3" />
              </linearGradient>
            ))}
          </defs>
          {mappings.map((mapping) => {
            const goal = goals.find(g => g.id === mapping.goal_id);
            if (!goal) return null;

            return mapping.rubric_items.map(itemId => {
              const startPoint = connectionPoints.get(`goal-${mapping.goal_id}`);
              const endPoint = connectionPoints.get(`rubric-${mapping.rubric_category}-${itemId}`);
              
              if (!startPoint || !endPoint) return null;

              const isActive = isConnectionActive(mapping.goal_id, mapping.rubric_category, itemId);
              const color = goalColors[goal.goal_type] || '#6b7280';
              // Show all connections by default, make selected ones bolder
              const opacity = isActive ? 0.8 : 0.15;
              const strokeWidth = isActive ? 3 : 1.5;

              // Calculate curved path
              const midX = (startPoint.position.x + endPoint.position.x) / 2;
              const controlPoint1 = { x: midX, y: startPoint.position.y };
              const controlPoint2 = { x: midX, y: endPoint.position.y };
              const path = `M ${startPoint.position.x} ${startPoint.position.y} C ${controlPoint1.x} ${controlPoint1.y}, ${controlPoint2.x} ${controlPoint2.y}, ${endPoint.position.x} ${endPoint.position.y}`;

              return (
                <path
                  key={`${mapping.goal_id}-${mapping.rubric_category}-${itemId}`}
                  d={path}
                  stroke={color}
                  strokeDasharray={isActive ? "none" : "5,5"}
                  strokeWidth={strokeWidth}
                  fill="none"
                  opacity={opacity}
                  className="transition-opacity duration-300"
                  style={{
                    transition: 'opacity 0.3s ease-out'
                  }}
                />
              );
            });
          })}
        </svg>

        <div className={`flex ${isZoomedOut ? 'gap-10' : 'gap-20'} justify-center transition-all duration-300`}>
          {/* Goals Column */}
          <div className={isZoomedOut ? 'w-48' : 'w-64'}>
            <h4 className={`${isZoomedOut ? 'text-[10px] mb-2' : 'text-xs mb-4'} font-semibold ${mutedTextColor} uppercase tracking-wider`}>
              YOUR GOALS
            </h4>
            
            <div className={isZoomedOut ? 'space-y-1' : 'space-y-3'}>
              {goals.map((goal) => {
                const connections = getConnectionsForGoal(goal.id);
                const hasConnections = connections.some(c => c.rubric_items.length > 0);
                const isSelected = selectedGoalId === goal.id;
                const color = goalColors[goal.goal_type] || '#6b7280';
                
                return (
                  <motion.div
                    key={goal.id}
                    id={`preview-goal-${goal.id}`}
                    whileHover={!isZoomedOut ? { scale: 1.02 } : {}}
                    whileTap={!isZoomedOut ? { scale: 0.98 } : {}}
                    onClick={() => handleGoalClick(goal.id)}
                    className={`relative ${isZoomedOut ? 'p-2' : 'p-3'} rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? `${selectedBg} border-blue-500 shadow-lg` 
                        : hasConnections
                          ? `${cardBg} ${borderColor} hover:border-gray-500`
                          : `${darkMode ? 'bg-gray-900' : 'bg-gray-100'} ${borderColor} opacity-50`
                    }`}
                  >
                    <div className="flex items-center gap-2 pr-4">
                      <span className={isZoomedOut ? 'text-sm' : 'text-xl'}>{goalEmojis[goal.goal_type]}</span>
                      <span className={`${isZoomedOut ? 'text-[11px]' : 'text-sm'} ${textColor} truncate`} title={goal.goal_text}>
                        {goal.goal_text}
                      </span>
                    </div>
                    {hasConnections && (
                      <div 
                        className={`connection-dot-goal absolute top-1/2 transform -translate-y-1/2 ${isZoomedOut ? 'w-2 h-2' : 'w-3 h-3'} rounded-full transition-all ${
                          isSelected ? `${isZoomedOut ? 'w-3 h-3' : 'w-4 h-4'}` : ''
                        }`}
                        style={{ 
                          right: isSelected ? (isZoomedOut ? '-6px' : '-8px') : (isZoomedOut ? '-4px' : '-6px'),
                          backgroundColor: color,
                          boxShadow: isSelected ? `0 0 8px ${color}` : 'none'
                        }}
                      />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Rubric Column */}
          <div className={isZoomedOut ? 'w-64' : 'w-80'}>
            <h4 className={`${isZoomedOut ? 'text-[10px] mb-2' : 'text-xs mb-4'} font-semibold ${mutedTextColor} uppercase tracking-wider`}>
              EVALUATION CRITERIA
            </h4>
            
            <div className={isZoomedOut ? 'space-y-3' : 'space-y-6'}>
              {rubricCategories.map((category) => (
                <div key={category.id}>
                  <h5 className={`${isZoomedOut ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold ${mutedTextColor} uppercase`}>
                    {category.name}
                  </h5>
                  <div className={isZoomedOut ? 'space-y-0.5' : 'space-y-1'}>
                    {category.items.map((item: any) => {
                      const goalId = getGoalIdForRubricItem(category.id, item.id);
                      const goal = goalId ? goals.find(g => g.id === goalId) : null;
                      const isConnected = !!goalId;
                      const isSelected = selectedRubricItem === `${category.id}-${item.id}`;
                      const color = goal ? goalColors[goal.goal_type] : '#6b7280';
                      
                      return (
                        <div
                          key={item.id}
                          id={`preview-rubric-${category.id}-${item.id}`}
                          onClick={() => handleRubricItemClick(category.id, item.id)}
                          className={`relative ${isZoomedOut ? 'p-1 pl-4' : 'p-2 pl-6'} rounded ${isZoomedOut ? 'text-xs' : 'text-sm'} cursor-pointer transition-all ${
                            isSelected
                              ? `${selectedBg} border-2 border-green-500 shadow-lg`
                              : isConnected
                                ? `${cardBg} hover:bg-gray-700`
                                : `${darkMode ? 'bg-gray-900' : 'bg-gray-100'} opacity-50`
                          }`}
                        >
                          {isConnected && (
                            <div 
                              className={`connection-dot-rubric absolute top-1/2 transform -translate-y-1/2 ${isZoomedOut ? 'w-2 h-2' : 'w-3 h-3'} rounded-full transition-all ${
                                isSelected ? `${isZoomedOut ? 'w-3 h-3' : 'w-4 h-4'}` : ''
                              }`}
                              style={{ 
                                left: isSelected ? (isZoomedOut ? '-6px' : '-8px') : (isZoomedOut ? '-4px' : '-6px'),
                                backgroundColor: color,
                                boxShadow: isSelected ? `0 0 8px ${color}` : 'none'
                              }}
                            />
                          )}
                          <span className={`${isConnected ? textColor : mutedTextColor} ${isZoomedOut ? 'text-[10px]' : 'text-xs'}`}>
                            {item.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MappingPreview;